import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
import { MIN_REJECT_REASON_LENGTH } from '../requisition/requisition.schema.js';
import { backorderStatusSchema } from './status.js';
const idSchema = z.string().regex(/^\d+$/);
/**
 * BILDIRGI — qoldiqda YO'Q mahsulot uchun ariza.
 *
 * Bitta bildirgi — bitta MAHSULOT. Savat qilinmadi: qabul qilish ham,
 * kelishi ham, biriktirish ham mahsulot bo'yicha ketadi va ko'p satrli
 * hujjat «yarmi keldi, yarmi yo'q» holatida o'zini ikkiga bo'lib tashlashi
 * kerak bo'lardi.
 *
 * Mahsulot IKKI yo'l bilan ko'rsatiladi:
 *   - `nomenclatureId` — katalogdan tanlangan, aniq;
 *   - `freeText` — katalogda topilmagan narsaning odam yozgan nomi.
 *
 * Erkin matnli bildirgini skladchi qabul qilayotganda XOHLASA katalogga
 * bog'laydi (`acceptBackorderSchema`). Bog'lamasa ham oqim to'xtamaydi:
 * biriktirishda u omborda bor istalgan mahsulotni tanlaydi.
 */
const backorderProductShape = {
    nomenclatureId: idSchema.nullish(),
    freeText: z.string().trim().min(3).max(200).nullish(),
};
/**
 * Mahsulot maydonlarini BITTA ko'rinishga keltiradi.
 *
 * Ikkovi ham berilsa KATALOG ustun va erkin matn tashlanadi: matn odamning
 * taxmini, nomenklatura esa aniq yozuv. Ikkalasi ham saqlansa, keyinchalik
 * «qaysi biri to'g'ri» degan javobsiz savol tug'ilardi.
 */
const normalizeProduct = (value, ctx) => {
    const nomenclatureId = value.nomenclatureId ?? null;
    const freeText = value.freeText ?? null;
    if (nomenclatureId === null && freeText === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nomenclatureId'],
            message: 'Mahsulotni tanlang yoki nomini yozing',
        });
    }
    return { nomenclatureId, freeText: nomenclatureId === null ? freeText : null };
};
/** BILDIRGI YUBORISH so'rovi. */
export const createBackorderSchema = z
    .object({
    ...backorderProductShape,
    warehouseId: idSchema,
    requestedQty: z.number().positive().max(1_000_000),
    /* Nima uchun kerak — skladchi ta'minotga buyurtma berayotganda aynan
       shuni o'qiydi. Bo'sh qoldirilsa, ariza «nimadir kerak» dan iborat
       bo'lib qolardi. */
    purpose: z.string().trim().min(3).max(500),
    /** Qachongacha kerak — ixtiyoriy: hamma ehtiyojning muddati bo'lavermaydi. */
    neededBy: z.coerce.date().nullish(),
    note: z.string().trim().max(500).nullish(),
})
    .transform((value, ctx) => ({
    ...normalizeProduct(value, ctx),
    warehouseId: value.warehouseId,
    requestedQty: value.requestedQty,
    purpose: value.purpose,
    neededBy: value.neededBy ?? null,
    note: value.note ?? null,
}));
/** Bir yuborishdagi mahsulotlar chegarasi — savat hujjat emas, ro'yxat ham cheksiz emas. */
export const MAX_BACKORDER_BATCH_ITEMS = 50;
const backorderBatchItemSchema = z
    .object({
    ...backorderProductShape,
    requestedQty: z.number().positive().max(1_000_000),
})
    .transform((value, ctx) => ({
    ...normalizeProduct(value, ctx),
    requestedQty: value.requestedQty,
}));
/**
 * BIR NECHTA BILDIRGI — bitta yuborishda.
 *
 * Ombor, muddat, maqsad va izoh UMUMIY: xodim bitta ish uchun bir nechta
 * narsa so'raydi va ularni har qatorda qayta yozishi bekor mehnat bo'lardi.
 * Har mahsulot baribir ALOHIDA bildirgi bo'lib saqlanadi — ular har xil
 * vaqtda keladi va skladchi har birini alohida qabul qiladi/biriktiradi.
 */
export const createBackorderBatchSchema = z
    .object({
    warehouseId: idSchema,
    purpose: z.string().trim().min(3).max(500),
    neededBy: z.coerce.date().nullish(),
    note: z.string().trim().max(500).nullish(),
    items: z.array(backorderBatchItemSchema).min(1).max(MAX_BACKORDER_BATCH_ITEMS),
})
    .transform((value) => ({
    warehouseId: value.warehouseId,
    purpose: value.purpose,
    neededBy: value.neededBy ?? null,
    note: value.note ?? null,
    items: value.items,
}));
/**
 * QABUL QILISH — ixtiyoriy ravishda katalog mahsulotiga bog'lash.
 *
 * Maydon IXTIYORIY va bu qaror: skladchi mahsulotni har doim ham katalogdan
 * topa olmaydi, lekin arizani qabul qilishi kerak. Bog'lanmagan bildirgi
 * uchun mahsulot biriktirish paytida, omborda bor narsadan tanlanadi.
 */
export const acceptBackorderSchema = z
    .object({ nomenclatureId: idSchema.nullish() })
    .transform((value) => ({ nomenclatureId: value.nomenclatureId ?? null }));
/** Rad etish — sabab MAJBURIY: usiz xodim nima qilishini bilmaydi. */
export const rejectBackorderSchema = z.object({
    reason: z.string().trim().min(MIN_REJECT_REASON_LENGTH).max(500),
});
/**
 * BIRIKTIRISH — ombordagi partiyadan buyurtmachiga ajratish.
 *
 * Mahsulot ALOHIDA yuborilmaydi — u partiyadan olinadi. Skladchi bildirgi
 * bog'langan mahsulotni emas, omborda bor ISTALGAN mahsulotni tanlashi
 * mumkin (o'xshash o'lcham, boshqa ishlab chiqaruvchi), va mijozdan kelgan
 * `nomenclatureId` partiyaga zid bo'lib qolishi mumkin edi.
 *
 * Miqdor so'raganidan KAM bo'lishi mumkin va bu oddiy hol: o'ntadan uchtasi
 * kelgan bo'lsa, skladchi uchtasini biriktiradi. Ko'p bo'lishi mumkin
 * emas — buni domen tekshiradi, sxema emas.
 */
export const fulfilBackorderSchema = z.object({
    batchId: idSchema,
    cellId: idSchema,
    qty: z.number().positive().max(1_000_000),
});
export const backorderQuerySchema = paginationQuerySchema.extend({
    status: backorderStatusSchema.optional(),
    warehouseId: idSchema.optional(),
});
/**
 * Ro'yxatdagi bitta qator.
 *
 * `nomenclatureName` va `unit` — katalogdan; erkin matnli, hali
 * biriktirilmagan bildirgida ikkalasi ham `null` bo'ladi va ekran
 * `freeText` ni ko'rsatadi. O'ylab topilgan birlik («dona» deb taxmin
 * qilish) keyinchalik noto'g'ri miqdorli talabnoma tug'ilishiga olib
 * kelardi.
 */
export const backorderRowSchema = z.object({
    id: z.string(),
    docNo: z.string(),
    status: backorderStatusSchema,
    warehouseId: z.string(),
    warehouseName: z.string(),
    nomenclatureId: z.string().nullable(),
    nomenclatureName: z.string().nullable(),
    code: z.string().nullable(),
    unit: z.string().nullable(),
    freeText: z.string().nullable(),
    requestedQty: z.number(),
    purpose: z.string(),
    neededBy: z.string().nullable(),
    note: z.string().nullable(),
    rejectReason: z.string().nullable(),
    requesterUserId: z.string(),
    requesterName: z.string(),
    requisitionId: z.string().nullable(),
    requisitionDocNo: z.string().nullable(),
    /**
     * Shu ombordagi ERKIN qoldiq — «Kutilmoqda» ro'yxatida, faqat katalogga
     * bog'langan bildirgi uchun to'ldiriladi.
     *
     * Boshqa ro'yxatlarda va bog'lanmagan bildirgida `null`: qoldiqni har bir
     * qator uchun hisoblash navbatni bekorga og'irlashtirardi, bog'lanmagan
     * bildirgida esa nimaning qoldig'ini hisoblash noma'lum.
     */
    availableQty: z.number().nullable(),
    acceptedAt: z.string().nullable(),
    fulfilledAt: z.string().nullable(),
    createdAt: z.string(),
    /**
     * TO'PLAM — bir yuborishda ketgan bildirgilarni bog'laydi.
     *
     * Xodim savatga bir necha mahsulot solib bitta tugma bosadi, lekin
     * har mahsulot alohida bildirgi bo'lib yashaydi. Skladchining
     * navbatida ular boshqa odamlarning arizalari orasiga sochilib
     * ketmasligi uchun shu bog'liqlik kerak.
     *
     * `null` — yakka ariza. Yakkaga to'plam ATAYLAB qo'yilmaydi: navbat
     * uni bir qatorlik "guruh" qilib chizardi va bir xil narsa ikki marta
     * aytilardi.
     */
    batchId: z.string().nullable(),
});
/** Bir biriktirishdagi bildirgilar chegarasi — talabnoma ham cheksiz emas. */
export const MAX_FULFIL_BATCH_ITEMS = 50;
/**
 * BIR NECHTA BILDIRGI — BITTA talabnomaga.
 *
 * Skladchi navbatdan bir nechtasini belgilaydi va hammasini bitta varaqa
 * qilib beradi. Ilgari har bildirgi o'z talabnomasini tug'dirardi: bir
 * odamga uchta narsa berilsa, u uchta hujjatga uch marta imzo qo'yardi va
 * buxgalteriya uchta alohida qog'oz olardi.
 *
 * Satrning tarkibi yakka biriktirish bilan AYNAN bir xil — faqat qaysi
 * bildirgi ekani qo'shiladi. Ikki sxema bir-biridan ajrab ketmasligi uchun
 * `fulfilBackorderSchema` shu yerda kengaytiriladi.
 */
export const fulfilBackorderBatchSchema = z.object({
    items: z
        .array(fulfilBackorderSchema.extend({ backorderId: idSchema }))
        .min(1)
        .max(MAX_FULFIL_BATCH_ITEMS),
});
//# sourceMappingURL=backorder.schema.js.map